/**
 * Streaming Response Enhancer for MCP Integration
 * Enhances existing streaming functionality with ANSI code handling and execution plan updates
 */

export interface ExecutionStep {
  id: string;
  name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
}

export interface CostInfo {
  total: number;
  breakdown?: number[];
  currency: string;
  services?: Array<{
    name: string;
    cost: number;
  }>;
}

export interface EnhancedStreamingCallbacks {
  onExecutionUpdate?: (step: string, status: string) => void;
  onCostUpdate?: (costData: CostInfo) => void;
  onAnsiContent?: (htmlContent: string) => void;
}

export class StreamingEnhancer {
  private ansiMap: { [key: string]: string };
  private executionSteps: ExecutionStep[];

  constructor() {
    this.ansiMap = {
      // Reset codes
      '\u001b[0m': '</span>',
      '\u001b[39m': '</span>',
      
      // Color codes (foreground)
      '\u001b[38;5;11m': '<span style="color: #ffff00;">',  // Yellow
      '\u001b[38;5;13m': '<span style="color: #ff00ff;">',  // Magenta  
      '\u001b[38;5;2m': '<span style="color: #00ff00;">',   // Green
      '\u001b[38;5;10m': '<span style="color: #00ff00;">',  // Bright Green
      '\u001b[31m': '<span style="color: #ff0000;">',       // Red
      '\u001b[32m': '<span style="color: #00ff00;">',       // Green
      '\u001b[33m': '<span style="color: #ffff00;">',       // Yellow
      '\u001b[34m': '<span style="color: #0000ff;">',       // Blue
      '\u001b[35m': '<span style="color: #ff00ff;">',       // Magenta
      '\u001b[36m': '<span style="color: #00ffff;">',       // Cyan
      '\u001b[37m': '<span style="color: #ffffff;">',       // White
      
      // Bold codes
      '\u001b[1m': '<span style="font-weight: bold;">',
      '\u001b[22m': '</span>',
    };

    this.executionSteps = [
      { id: 'connect', name: 'Connect to AWS Cost Explorer', status: 'pending' },
      { id: 'setup', name: 'Set up cost query for period', status: 'pending' }, 
      { id: 'retrieve', name: 'Retrieve comprehensive cost data and metrics', status: 'pending' },
      { id: 'process', name: 'Process and format cost analysis report', status: 'pending' },
      { id: 'insights', name: 'Provide comprehensive cost insights and summary', status: 'pending' }
    ];
  }

  /**
   * Convert ANSI codes to HTML
   */
  convertAnsiToHtml(text: string): string {
    let html = text;
    
    // Convert ANSI codes to HTML spans
    for (const [ansi, htmlTag] of Object.entries(this.ansiMap)) {
      html = html.replace(new RegExp(this.escapeRegex(ansi), 'g'), htmlTag);
    }
    
    return html;
  }

  /**
   * Escape regex special characters
   */
  private escapeRegex(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Enhance streaming content with ANSI conversion and execution updates
   */
  enhanceStreamingContent(
    content: string, 
    callbacks: EnhancedStreamingCallbacks = {}
  ): string {
    // Convert ANSI codes to HTML
    const htmlContent = this.convertAnsiToHtml(content);
    
    // Update execution plan based on content
    this.updateExecutionPlan(content, callbacks.onExecutionUpdate);
    
    // Extract cost information
    this.extractCostInfo(content, callbacks.onCostUpdate);
    
    // Trigger ANSI content callback
    if (callbacks.onAnsiContent) {
      callbacks.onAnsiContent(htmlContent);
    }
    
    return htmlContent;
  }

  /**
   * Update execution plan based on response content
   */
  private updateExecutionPlan(text: string, onExecutionUpdate?: (step: string, status: string) => void) {
    // Detect AWS CLI command execution
    if (text.includes('🛠️  Using tool: use_aws')) {
      this.updateStep('connect', 'in_progress');
      onExecutionUpdate?.('connect', 'in_progress');
    }

    // Detect cost query setup
    if (text.includes('Service name: ce') && text.includes('get-cost-and-usage')) {
      this.updateStep('connect', 'completed');
      this.updateStep('setup', 'in_progress');
      onExecutionUpdate?.('setup', 'in_progress');
    }

    // Detect data retrieval completion
    if (text.includes('● Completed in')) {
      this.updateStep('setup', 'completed');
      this.updateStep('retrieve', 'completed');
      this.updateStep('process', 'in_progress');
      onExecutionUpdate?.('retrieve', 'completed');
      onExecutionUpdate?.('process', 'in_progress');
    }

    // Detect final insights
    if (text.includes('Based on') || text.includes('your total') || text.includes('Would you like me to')) {
      this.updateStep('process', 'completed');
      this.updateStep('insights', 'completed');
      onExecutionUpdate?.('insights', 'completed');
    }
  }

  /**
   * Extract cost information from response
   */
  private extractCostInfo(text: string, onCostUpdate?: (costData: CostInfo) => void) {
    if (!onCostUpdate) return;

    // Extract total cost
    const costMatches = text.match(/\$(\d+\.?\d*)/g);
    if (costMatches) {
      const costs = costMatches.map(c => parseFloat(c.replace('$', '')));
      const totalCost = costs.reduce((sum, cost) => sum + cost, 0);
      
      onCostUpdate({
        total: totalCost,
        breakdown: costs.length > 1 ? costs : undefined,
        currency: 'USD'
      });
    }

    // Extract service breakdown
    if (text.includes('Amazon EC2') || text.includes('EC2 -')) {
      const services = this.extractServiceCosts(text);
      if (services.length > 0) {
        onCostUpdate({
          total: services.reduce((sum, service) => sum + service.cost, 0),
          currency: 'USD',
          services
        });
      }
    }
  }

  /**
   * Extract service costs from text
   */
  private extractServiceCosts(text: string): Array<{ name: string; cost: number }> {
    const services: Array<{ name: string; cost: number }> = [];
    const serviceLines = text.split('\n').filter(line => 
      line.includes('Amazon ') || line.includes('EC2') || line.includes('AWS ')
    );

    for (const line of serviceLines) {
      const match = line.match(/(Amazon\s+\w+|EC2[^:]*|AWS[^:]*)[:\s]+\$(\d+\.?\d*)/);
      if (match) {
        services.push({
          name: match[1].trim(),
          cost: parseFloat(match[2])
        });
      }
    }

    return services;
  }

  /**
   * Update execution step status
   */
  private updateStep(stepId: string, status: ExecutionStep['status']) {
    const step = this.executionSteps.find(s => s.id === stepId);
    if (step) {
      step.status = status;
    }
  }

  /**
   * Get current execution plan state
   */
  getExecutionPlan(): ExecutionStep[] {
    return this.executionSteps.map(step => ({ ...step }));
  }

  /**
   * Reset execution plan for new query
   */
  resetExecutionPlan(): void {
    this.executionSteps.forEach(step => {
      step.status = 'pending';
    });
  }

  /**
   * Set execution steps (for dynamic todo lists from backend)
   */
  setExecutionSteps(steps: ExecutionStep[]): void {
    this.executionSteps = steps.map(step => ({ ...step }));
  }

  /**
   * Update step status by index
   */
  updateStepByIndex(index: number, status: ExecutionStep['status']): void {
    if (index >= 0 && index < this.executionSteps.length) {
      this.executionSteps[index].status = status;
    }
  }

  /**
   * Create CSS styles for ANSI colors
   */
  static getAnsiCssStyles(): string {
    return `
      .ansi-content {
        font-family: 'Courier New', monospace;
        white-space: pre-wrap;
        line-height: 1.6;
      }
      .ansi-content span[style*="color: #ffff00"] {
        color: #ffff00 !important;
      }
      .ansi-content span[style*="color: #ff00ff"] {
        color: #ff00ff !important;
      }
      .ansi-content span[style*="color: #00ff00"] {
        color: #00ff00 !important;
      }
      .ansi-content span[style*="font-weight: bold"] {
        font-weight: bold !important;
      }
    `;
  }
}

// Global instance for use across the app
export const streamingEnhancer = new StreamingEnhancer();